import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { clinicAPI } from '@/modules/clinic/api/clinic.api';
import { queryKeys } from '@/shared/query';
import {
  canViewAllClinics,
  getAllowedClinicIds,
  resolveClinicScopeForUser,
} from '@/shared/utils/clinicScopeAccess';

interface Clinic {
  _id: string;
  name: string;
  [key: string]: any;
}

interface ClinicScopeContextType {
  clinics: Clinic[];
  selected: string;
  setSelected: (clinicId: string) => void;
  canViewAllClinics: boolean;
}

const ClinicScopeContext = createContext<ClinicScopeContextType | undefined>(undefined);

interface ClinicScopeProviderProps {
  children: ReactNode;
}

const readStoredClinicScope = (): string => {
  try {
    return localStorage.getItem('clinicScope') || 'all';
  } catch {
    return 'all';
  }
};

export const ClinicScopeProvider: React.FC<ClinicScopeProviderProps> = ({ children }) => {
  const { token, user } = useAuth();
  const [selected, setSelected] = useState<string>(readStoredClinicScope);

  const canFetchClinics = Boolean(token && user && user.role !== 'super_admin');
  // Shared with other clinic pickers so create/edit/delete refresh the AppBar list.
  const clinicsQuery = useQuery({
    queryKey: queryKeys.clinic.list(),
    queryFn: async () => {
      const res = await clinicAPI.getAll();
      return (res.data || []) as Clinic[];
    },
    enabled: canFetchClinics,
  });

  const clinics = canFetchClinics ? (clinicsQuery.data ?? []) : [];

  const userCanViewAll = canViewAllClinics(user);

  const visibleClinics = useMemo(() => {
    const allowedIds = getAllowedClinicIds(user);
    if (!allowedIds) return clinics;
    const allowedSet = new Set(allowedIds);
    return clinics.filter((c) => allowedSet.has(c._id));
  }, [clinics, user]);

  const resolvedSelected = useMemo(() => {
    const resolved = resolveClinicScopeForUser(user, selected);
    // Single-clinic org: scope is that clinic — no meaningful "all" choice
    if (visibleClinics.length === 1) {
      return visibleClinics[0]._id;
    }
    return resolved;
  }, [user, selected, visibleClinics]);

  useLayoutEffect(() => {
    if (!user) return;

    const resolved = resolveClinicScopeForUser(user, readStoredClinicScope());
    setSelected((current) => (current === resolved ? current : resolved));
    try {
      localStorage.setItem('clinicScope', resolved);
    } catch {
      // Ignore storage errors
    }
  }, [user, user?.clinicAccess?.type, user?.clinicAccess?.clinics]);

  useEffect(() => {
    try {
      localStorage.setItem('clinicScope', resolvedSelected);
    } catch {
      // Ignore storage errors
    }
  }, [resolvedSelected]);

  useEffect(() => {
    window.dispatchEvent(new Event('clinic-scope-changed'));
  }, [resolvedSelected]);

  const value = useMemo(
    () => ({
      clinics: visibleClinics,
      selected: resolvedSelected,
      setSelected,
      canViewAllClinics: userCanViewAll,
    }),
    [visibleClinics, resolvedSelected, userCanViewAll]
  );

  return <ClinicScopeContext.Provider value={value}>{children}</ClinicScopeContext.Provider>;
};

export const useClinicScope = (): ClinicScopeContextType => {
  const context = useContext(ClinicScopeContext);
  if (!context) {
    throw new Error('useClinicScope must be used within a ClinicScopeProvider');
  }
  return context;
};

export default ClinicScopeContext;
